import { useId, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Label,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Text } from '@buffer-mono/popcorn'

import { ChartFrame } from '../ChartFrame'
import { useChartSeries } from '../ChartSeriesContext'
import { useChartChrome } from '../cartesianChart'
import { formatChartValue } from '../chartFormatting'
import type { MetricUnit } from '../types'
import { BarCategoryTick } from '../BarChart/BarCategoryTick'

import { buildBreakdownRows } from './buildBreakdownRows'
import type { BreakdownRow } from './buildBreakdownRows'
import { BreakdownDataTable } from './BreakdownDataTable'
import { BreakdownTooltipView } from './BreakdownTooltipView'

import styles from './BreakdownBarChart.module.css'

// Layout — a fixed row height with a floor so a one- or two-row chart doesn't
// collapse. Bars run the full value width, so the right margin leaves room for
// the trailing value tick and the bar's rounded end cap.
const ROW_HEIGHT = 40
const MIN_HEIGHT = 160
const CHART_VERTICAL_PADDING = 24
const CATEGORY_AXIS_WIDTH = 116
const CHART_MARGIN = { top: 4, bottom: 4, left: 8, right: 72 }
const BAR_RADIUS = 4
const ROUNDED_RIGHT: [number, number, number, number] = [
  0,
  BAR_RADIUS,
  BAR_RADIUS,
  0,
]
// The change segment (gain/loss) is painted at a lighter opacity so it reads as a
// distinct portion stacked on the solid base.
const CHANGE_OPACITY = 0.45
// A decline's change segment uses a distinct red so a loss reads differently from
// a channel-coloured gain.
const LOST_COLOR = 'var(--global-color-red-600)'
// A labelled value axis reserves a taller band (tick row + caption below it), and
// the chart grows by the same allowance so the bars don't shrink to make room.
const VALUE_AXIS_HEIGHT = 48
const VALUE_AXIS_LABEL_ALLOWANCE = 28

const chartHeight = (rowCount: number): number =>
  Math.max(rowCount * ROW_HEIGHT + CHART_VERTICAL_PADDING, MIN_HEIGHT)

interface BreakdownBarShapeProps {
  /** Geometry + data injected by Recharts when used as the Bar `shape`. */
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: BreakdownRow
  /** Resolved base (channel) colour per series id (from {@link useChartChrome}). */
  baseColorById?: Record<string, string>
  /** Colour token for a decline's change segment. */
  lostColor: string
  /** Unique-per-chart prefix for the clip-path id (rows append their own id). */
  clipPrefix: string
}

/**
 * Per-row bar renderer. Rounds the whole bar's trailing end regardless of the
 * base/change split via a clip path; the solid base (the lower of start/current)
 * and the lighter gain / red loss segment are painted side by side inside it. Colour is applied
 * via CSS `fill` (not the SVG attribute) so it re-themes in dark mode.
 */
function BreakdownBarShape({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  payload,
  baseColorById = {},
  lostColor,
  clipPrefix,
}: BreakdownBarShapeProps): JSX.Element {
  if (payload == null) return <g />
  const total = payload.base + payload.change
  if (total === 0) return <g />

  const baseColor = baseColorById[payload.id]

  // Flat bar (no change): a single rounded solid rect.
  if (payload.change === 0) {
    return (
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        radius={ROUNDED_RIGHT}
        style={{ fill: baseColor }}
      />
    )
  }

  // base + change share the 0-based value scale, so split the pixel width by ratio.
  const baseWidth = (payload.base / total) * width
  const changeFill = payload.isGain ? baseColor : lostColor
  const clipId = `${clipPrefix}-${payload.id}`
  return (
    <g>
      <clipPath id={clipId}>
        <Rectangle
          x={x}
          y={y}
          width={width}
          height={height}
          radius={ROUNDED_RIGHT}
        />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <rect
          x={x}
          y={y}
          width={baseWidth}
          height={height}
          style={{ fill: baseColor }}
        />
        <rect
          x={x + baseWidth}
          y={y}
          width={width - baseWidth}
          height={height}
          style={{ fill: changeFill }}
          fillOpacity={CHANGE_OPACITY}
        />
      </g>
    </g>
  )
}

interface ValueAxisTickProps {
  /** Geometry + tick data injected by Recharts when used as the XAxis `tick`. */
  x?: number
  y?: number
  payload?: { value?: number }
  /** Formats the value with the series' unit (counts vs percentage). */
  unit?: MetricUnit
}

/**
 * Bottom value-axis tick. Subtle label with the fill set via CSS so it resolves /
 * re-themes in dark mode (the default Recharts tick uses a hardcoded colour).
 */
function ValueAxisTick({
  x = 0,
  y = 0,
  payload,
  unit,
}: ValueAxisTickProps): JSX.Element {
  return (
    <text
      x={x}
      y={y}
      dy="0.71em"
      textAnchor="middle"
      fontSize={12}
      style={{ fill: 'var(--color-text-subtle)' }}
    >
      {formatChartValue(payload?.value ?? 0, unit)}
    </text>
  )
}

export interface BreakdownBarChartProps {
  /** Accessible name for the chart (e.g. "Followers by channel"). */
  ariaLabel: string
  /** Overlay shown when there's no data; the axes stay visible behind it. */
  emptyState?: ReactNode
  /** Disable enter animations *and* the category-tick hover tooltip — both are
   *  switched off together for the offscreen export snapshot (which renders with
   *  no `TooltipProvider`) and for stable tests. */
  disableAnimations?: boolean
  /** Optional caption centred under the value (X) axis, e.g. "Followers". No
   *  default: a ranked chart's series labels are channel names, not the metric. */
  xAxisLabel?: string
}

/**
 * The base breakdown bar chart: one horizontal bar per series, ranked by current
 * value, with each bar split into a solid base (the lower of start and current)
 * and a lighter change segment stacked on top (growth, or a red loss). Answers "who's
 * biggest" *and* "how much did each move" in one view. Net change is derived from
 * each series' time series, so it summarises cumulative metrics like Followers.
 * Series — and their colour/label/icon — come from {@link ChartSeriesContext};
 * colour is never a prop. The split is carried by the tooltip + screen-reader data
 * table (via {@link ChartFrame}), never by colour alone, and it's keyboard-navigable.
 */
export function BreakdownBarChart({
  ariaLabel,
  emptyState,
  disableAnimations = false,
  xAxisLabel,
}: BreakdownBarChartProps): JSX.Element {
  const series = useChartSeries()
  const { resolvedColors, gridColor, subtleTextColor } = useChartChrome(series)

  const rows = useMemo(() => buildBreakdownRows(series), [series])
  // Every series in one ranked chart shares a metric/unit, so the value axis +
  // ticks format off the first series' unit.
  const unit = series[0]?.unit

  // Stable per-chart-instance prefix so each bar's clip-path id is unique even
  // with multiple charts on a page. Colons from useId aren't valid in url(#…).
  const clipPrefix = `bd${useId().replace(/:/g, '')}`

  const hasGains = rows.some((r) => r.isGain && r.change > 0)
  const hasLosses = rows.some((r) => !r.isGain && r.change > 0)

  const height =
    chartHeight(rows.length) +
    (xAxisLabel != null ? VALUE_AXIS_LABEL_ALLOWANCE : 0)

  return (
    <>
      <ChartFrame
        ariaLabel={ariaLabel}
        emptyState={emptyState}
        height={height}
        dataTable={<BreakdownDataTable caption={ariaLabel} />}
      >
        <RechartsBarChart
          data={rows}
          layout="vertical"
          margin={CHART_MARGIN}
          accessibilityLayer
          aria-label={ariaLabel}
        >
          <CartesianGrid horizontal={false} stroke={gridColor} />

          <XAxis
            type="number"
            tick={<ValueAxisTick unit={unit} />}
            axisLine={false}
            tickLine={false}
            height={xAxisLabel != null ? VALUE_AXIS_HEIGHT : undefined}
          >
            {xAxisLabel != null && (
              <Label
                value={xAxisLabel}
                position="insideBottom"
                offset={0}
                style={{
                  fill: subtleTextColor,
                  fontSize: 12,
                  textAnchor: 'middle',
                }}
              />
            )}
          </XAxis>

          <YAxis
            type="category"
            dataKey="id"
            width={CATEGORY_AXIS_WIDTH}
            axisLine={false}
            tickLine={false}
            tick={<BarCategoryTick disableTooltip={disableAnimations} />}
          />

          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={(props) => <BreakdownTooltipView {...props} />}
          />

          <Bar
            dataKey={(row: BreakdownRow) => row.base + row.change}
            isAnimationActive={!disableAnimations}
            shape={
              <BreakdownBarShape
                baseColorById={resolvedColors}
                lostColor={LOST_COLOR}
                clipPrefix={clipPrefix}
              />
            }
          />
        </RechartsBarChart>
      </ChartFrame>
      {(hasGains || hasLosses) && (
        // Explains the two-segment encoding to sighted users; decorative, since
        // the data table carries the exact start / change / current values.
        <div className={styles.legend} aria-hidden="true">
          <span className={styles.legendItem}>
            <span className={styles.swatch} />
            <Text size="sm">{hasLosses ? 'Baseline' : 'At range start'}</Text>
          </span>
          {hasGains && (
            <span className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.swatchLight}`} />
              <Text size="sm">Gained in range</Text>
            </span>
          )}
          {hasLosses && (
            <span className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.swatchLost}`} />
              <Text size="sm">Lost in range</Text>
            </span>
          )}
        </div>
      )}
    </>
  )
}
